import React, { useState, useEffect } from "react";
import {
  MapPin,
  Clock,
  Phone,
  Globe,
  Instagram,
  Facebook,
  Twitter,
  ExternalLink,
  ChevronLeft,
  ChevronRight,
  Share2,
  Eye,
} from "lucide-react";
import { useNavigate, useParams } from "react-router-dom";
import {
  Box,
  Paper,
  Typography,
  Button,
  IconButton,
  Stack,
  Chip,
  Snackbar,
  Alert,
  Link as MuiLink,
} from "@mui/material";
import { styled } from "@mui/material/styles";
import Match from "../media/match.png";
import { API_BASE_URL, authenticatedFetch } from "../api";
import RestaurantAltImage from "../media/restaurant-alt-image.png";
import { usePostHog } from "../Posthogprovider.jsx";
import DeliveryLinks from "./DeliveryLinks.js";
import InstallButton from "./InstallButton.js";

// ---------- Styled components ----------

// Main card container
const Frame = styled(Paper)(({ theme }) => ({
  width: "100%",
  boxShadow: theme.shadows[10],
  overflow: "hidden",
  display: "flex",
  flexDirection: "column",
}));

// Demo Mode Banner
const DemoBanner = styled(Box)(({ theme }) => ({
  background: "linear-gradient(135deg, #fef3c7, #fde68a)",
  borderBottom: "2px solid #fbbf24",
  padding: theme.spacing(1.5, 3),
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  gap: theme.spacing(1),
}));

const DemoBannerText = styled(Typography)(({ theme }) => ({
  fontWeight: 700,
  fontSize: "0.85rem",
  color: "#92400e",
  letterSpacing: "0.05em",
  display: "flex",
  alignItems: "center",
  gap: theme.spacing(1),
}));

// Demo Info Box
const DemoInfoBox = styled(Box)(({ theme }) => ({
  backgroundColor: "#eff6ff",
  border: "2px solid #3b82f6",
  borderRadius: theme.spacing(2),
  padding: theme.spacing(2.5),
  marginBottom: theme.spacing(3),
}));

const DemoInfoTitle = styled(Typography)(({ theme }) => ({
  fontSize: "0.95rem",
  fontWeight: 700,
  color: "#1e40af",
  marginBottom: theme.spacing(1),
  display: "flex",
  alignItems: "center",
  gap: theme.spacing(1),
}));

const DemoInfoText = styled(Typography)(({ theme }) => ({
  fontSize: "0.85rem",
  color: "#1e40af",
  lineHeight: 1.6,
  marginBottom: theme.spacing(1),
  "&:last-of-type": {
    marginBottom: 0,
  },
}));

// Banner (now just an image)
const Banner = styled(Box)(({ theme }) => ({
  background: "linear-gradient(90deg, #22C55E 0%, #059669 100%)",
  padding: theme.spacing(2, 3),
  textAlign: "center",
}));

const BannerImage = styled("img")(({ theme }) => ({
  width: "230px",
  height: "auto",
  display: "block",
  margin: "0 auto",
}));

// Scrollable area
const ScrollArea = styled(Box)(() => ({
  overflowY: "auto",
}));

// Image gallery
const ImageContainer = styled(Box)(({ theme }) => ({
  position: "relative",
  height: 256,
  backgroundColor: theme.palette.grey[900],
}));

const GalleryImage = styled("img")({
  width: "100%",
  height: "100%",
  objectFit: "cover",
  display: "block",
});

const GalleryNavButtonBase = styled(IconButton)(({ theme }) => ({
  position: "absolute",
  top: "50%",
  transform: "translateY(-50%)",
  backgroundColor: "rgba(0,0,0,0.5)",
  color: "#fff",
  "&:hover": {
    backgroundColor: "rgba(0,0,0,0.7)",
  },
}));

const GalleryNavButtonLeft = styled(GalleryNavButtonBase)(({ theme }) => ({
  left: 8,
}));

const GalleryNavButtonRight = styled(GalleryNavButtonBase)(({ theme }) => ({
  right: 8,
}));

const Indicators = styled(Box)(({ theme }) => ({
  position: "absolute",
  bottom: theme.spacing(2),
  left: "50%",
  transform: "translateX(-50%)",
  display: "flex",
  gap: theme.spacing(1),
}));

const IndicatorDot = styled(Box, {
  shouldForwardProp: (prop) => prop !== "active",
})(({ active }) => ({
  height: 8,
  borderRadius: 999,
  transition: "all 0.2s ease",
  width: active ? 32 : 8,
  backgroundColor: active ? "#fff" : "rgba(255,255,255,0.5)",
}));

// Tags
const TagChip = styled(Chip)(({ theme }) => ({
  backgroundColor: "#FFEDD5",
  color: "#C05621",
  fontWeight: 500,
}));

const TagContainer = styled(Box)(({ theme }) => ({
  display: "flex",
  flexWrap: "wrap",
  gap: theme.spacing(1),
}));

// Actions
const PrimaryActionButton = styled(Button)(({ theme }) => ({
  width: "100%",
  padding: theme.spacing(1.5),
  borderRadius: theme.spacing(2),
  fontWeight: 700,
  fontSize: "1rem",
  textTransform: "none",
  background: "linear-gradient(90deg, #F97316 0%, #EF4444 100%)",
  boxShadow: theme.shadows[6],
  color: "#fff !important",
  "&:hover": {
    boxShadow: theme.shadows[10],
    background: "linear-gradient(90deg, #FB923C 0%, #F97373 100%)",
  },
}));

const SecondaryActionButton = styled(Button)(({ theme }) => ({
  width: "100%",
  padding: theme.spacing(1.5),
  borderRadius: theme.spacing(2),
  fontWeight: 700,
  fontSize: "1rem",
  textTransform: "none",
  borderWidth: 2,
  borderStyle: "solid",
  borderColor: theme.palette.warning.main,
  color: theme.palette.warning.main,
  backgroundColor: "#fff",
  "&:hover": {
    backgroundColor: "#FFF7ED",
  },
}));

const StartOverButton = styled(Button)(({ theme }) => ({
  width: "100%",
  padding: theme.spacing(1.5),
  borderRadius: theme.spacing(2),
  fontWeight: 600,
  fontSize: "1rem",
  textTransform: "none",
  border: `2px solid ${theme.palette.grey[400]}`,
  color: theme.palette.grey[700],
  backgroundColor: "#fff",
  "&:hover": {
    backgroundColor: theme.palette.grey[100],
  },
}));

// Hours
const HoursHeaderRow = styled(Stack)(({ theme }) => ({
  marginBottom: theme.spacing(1.5),
}));

const TodayRow = styled(Box)(({ theme }) => ({
  display: "flex",
  justifyContent: "space-between",
  padding: theme.spacing(1, 1.5),
  borderRadius: theme.shape.borderRadius * 2,
  backgroundColor: "#FFEDD5",
  fontWeight: 600,
  color: "#C05621",
}));

const HoursToggleButton = styled(Button)(({ theme }) => ({
  padding: 0,
  marginTop: theme.spacing(0.5),
  textTransform: "none",
  fontWeight: 600,
  color: theme.palette.warning.main,
  alignSelf: "flex-start",
}));

const ShareButton = styled(IconButton)(({ theme }) => ({
  width: 36,
  height: 36,
  backgroundColor: "#F97316",
  color: "#fff",
  boxShadow: theme.shadows[4],
  "&:hover": {
    backgroundColor: "#EA580C",
    boxShadow: theme.shadows[8],
  },
  borderRadius: theme.shape.borderRadius * 2,
}));

// Social
const SocialButton = styled("a")(({ theme }) => ({
  width: 36,
  height: 36,
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  borderRadius: theme.shape.borderRadius * 2,
  color: "#fff",
  textDecoration: "none",
  boxShadow: theme.shadows[4],
  "&:hover": {
    boxShadow: theme.shadows[8],
  },
}));

const InstagramButton = styled(SocialButton)(({ theme }) => ({
  background: "linear-gradient(135deg, #EC4899 0%, #A855F7 50%, #F97316 100%)",
}));

const FacebookButton = styled(SocialButton)(({ theme }) => ({
  backgroundColor: "#2563EB",
}));

const TwitterButton = styled(SocialButton)(({ theme }) => ({
  backgroundColor: "#0EA5E9",
}));

// Links
const PhoneLink = styled(MuiLink)(({ theme }) => ({
  fontWeight: 600,
  color: theme.palette.text.primary,
  textDecoration: "none",
  "&:hover": {
    color: theme.palette.warning.main,
  },
}));

const WebsiteLink = styled(MuiLink)(({ theme }) => ({
  fontWeight: 600,
  color: theme.palette.text.primary,
  textDecoration: "none",
  display: "inline-flex",
  alignItems: "center",
  gap: 4,
  wordBreak: "break-all",
  "&:hover": {
    color: theme.palette.warning.main,
  },
}));

// Content wrapper
const Content = styled(Box)(({ theme }) => ({
  padding: theme.spacing(3),
}));

// ---------- Helper functions for Foursquare shape ----------

const priceToSymbols = (price) => {
  if (!price || price < 1) return "";
  const capped = Math.min(Math.max(price, 1), 4);
  return "$".repeat(capped);
};

const buildPhotoUrls = (photos) => {
  if (!photos || !photos.length) return [];
  return photos.map((p) => `${p.prefix}800x600${p.suffix}`);
};

const formatTime = (hhmm) => {
  if (!hhmm || hhmm.length !== 4) return hhmm || "";
  const h = parseInt(hhmm.slice(0, 2), 10);
  const m = hhmm.slice(2);
  const period = h >= 12 ? "PM" : "AM";
  const hour12 = ((h + 11) % 12) + 1;
  return `${hour12}:${m} ${period}`;
};

const buildHoursMap = (hours) => {
  if (!hours || !hours.regular || !Array.isArray(hours.regular)) return null;

  const days = [
    "Monday",
    "Tuesday",
    "Wednesday",
    "Thursday",
    "Friday",
    "Saturday",
    "Sunday",
  ];
  const map = {};

  hours.regular.forEach((entry) => {
    const dayIdx = Number(entry.day) - 1;
    if (dayIdx < 0 || dayIdx >= days.length) return;
    const dayName = days[dayIdx];
    const open = formatTime(entry.open);
    const close = formatTime(entry.close);
    const range = `${open} - ${close}`;
    if (!map[dayName]) {
      map[dayName] = range;
    } else {
      map[dayName] += `, ${range}`;
    }
  });

  return map;
};

const normalizeMatchPlace = (place) => {
  if (!place) return null;

  const primaryCategory = place.categories?.[0];
  const rating = place.rating ? (place.rating / 2).toFixed(1) : null;
  const reviewCount = place.stats?.total_ratings || null;
  const priceRange = priceToSymbols(place.price);
  const images = buildPhotoUrls(place.photos);
  const location = place.location || {};
  const geocodes = place.geocodes?.main || place.geocodes?.roof || null;

  const address = location.address || location.formatted_address || "";
  const cityLine = location.formatted_address
    ? location.formatted_address.replace(address + ", ", "")
    : [location.locality, location.region, location.postcode]
        .filter(Boolean)
        .join(", ");

  const social = place.social_media || {};
  const instagram = social.instagram || "";
  const facebook = social.facebook || "";
  const twitter = social.twitter || "";

  const hoursMap = buildHoursMap(place.hours);
  const description =
    place.description ||
    location.formatted_address ||
    primaryCategory?.name ||
    "";

  const tags = (place.categories || []).map((c) => c.name);

  let mapsUrl = "";
  if (geocodes?.latitude && geocodes?.longitude) {
    mapsUrl = `https://www.google.com/maps/search/?api=1&query=${geocodes.latitude},${geocodes.longitude}`;
  } else if (location.formatted_address) {
    mapsUrl = `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(
      location.formatted_address
    )}`;
  }

  return {
    name: place.name,
    cuisine: primaryCategory?.name || "Restaurant",
    rating,
    reviewCount,
    priceRange,
    images: images.length ? images : [],
    description,
    address,
    city: cityLine,
    phone: place.tel || "",
    website: place.website || "",
    hours: hoursMap,
    hoursDisplay: place.hours?.display || "",
    social: {
      instagram,
      facebook,
      twitter,
    },
    distance: "",
    tags,
    mapsUrl,
    menuUrl: place.menu || place.website || "",
  };
};

const getCurrentDayName = () => {
  const days = [
    "Sunday",
    "Monday",
    "Tuesday",
    "Wednesday",
    "Thursday",
    "Friday",
    "Saturday",
  ];
  return days[new Date().getDay()];
};

// ---------- Component ----------

const MatchPage = () => {
  const navigate = useNavigate();
  const { sessionId } = useParams();

  const [restaurant, setRestaurant] = useState(null);
  const [currentImageIndex, setCurrentImageIndex] = useState(0);
  const [showAllHours, setShowAllHours] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [isDemoMode, setIsDemoMode] = useState(false);
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: "",
    severity: "success",
  });

  const posthog = usePostHog();

  // Track when match page is viewed
  useEffect(() => {
    if (restaurant) {
      posthog.capture("match_page_viewed", {
        session_id: sessionId,
        restaurant_name: restaurant.name,
        cuisine: restaurant.cuisine,
        rating: restaurant.rating,
        is_demo: isDemoMode,
      });
    }
  }, [restaurant, sessionId, isDemoMode, posthog]);

  // Fetch session + matchedPlaceDetails
  useEffect(() => {
    const fetchMatch = async () => {
      if (!sessionId) return;

      try {
        setLoading(true);
        setError("");

        const res = await authenticatedFetch(
          `${API_BASE_URL}/sessions/${sessionId}`
        );
        if (!res.ok) {
          const text = await res.text();
          console.error(
            "Error fetching session/your-restaurant:",
            res.status,
            text
          );
          setError("Unable to load your match. Please try again.");
          setLoading(false);
          return;
        }

        const data = await res.json();
        console.log("MatchPage: session response:", data);

        const session = data.session || data;
        const place = session.matchedPlaceDetails;

        // Detect demo mode
        const isDemo =
          session.participantCount === 1 || session.party_size === 1;
        setIsDemoMode(isDemo);

        if (!place) {
          console.warn("MatchPage: no matchedPlaceDetails found on session.");
          setError("No match details found for this session yet.");
          setLoading(false);
          return;
        }

        const normalized = normalizeMatchPlace(place);
        setRestaurant(normalized);
        setCurrentImageIndex(0);
        setLoading(false);
      } catch (err) {
        console.error("MatchPage: error loading match:", err);
        setError("Something went wrong loading your match.");
        setLoading(false);
      }
    };

    fetchMatch();
  }, [sessionId]);

  const nextImage = () => {
    if (!restaurant || !restaurant.images?.length) return;
    setCurrentImageIndex((prev) => (prev + 1) % restaurant.images.length);
  };

  const prevImage = () => {
    if (!restaurant || !restaurant.images?.length) return;
    setCurrentImageIndex(
      (prev) => (prev - 1 + restaurant.images.length) % restaurant.images.length
    );
  };

  const currentDay = getCurrentDayName();

  const handleDirections = () => {
    if (restaurant?.mapsUrl) {
      window.open(restaurant.mapsUrl, "_blank", "noopener,noreferrer");
    }

    posthog.capture("directions_clicked", {
      session_id: sessionId,
      restaurant_name: restaurant.name,
      is_demo: isDemoMode,
    });
  };

  const handleViewMenu = () => {
    if (restaurant?.menuUrl) {
      const url = restaurant.menuUrl.startsWith("http")
        ? restaurant.menuUrl
        : `https://${restaurant.menuUrl}`;
      window.open(url, "_blank", "noopener,noreferrer");
    }

    posthog.capture("menu_clicked", {
      session_id: sessionId,
      restaurant_name: restaurant.name,
      is_demo: isDemoMode,
    });
  };

  const handleStartOver = () => {
    window.location.href = "/";

    posthog.capture("start_over_from_match_page", {
      session_id: sessionId,
      is_demo: isDemoMode,
    });
  };

  const handleShare = async () => {
    const shareData = {
      title: "Tell your friends about Foodie Call",
      text: `Still wasting time deciding where to eat? I just matched with ${restaurant.name} in seconds with Foodie Call! Try it now →`,
      url: window.location.origin,
    };

    try {
      if (
        navigator.share &&
        navigator.canShare &&
        navigator.canShare(shareData)
      ) {
        await navigator.share(shareData);

        posthog.capture("share_clicked", {
          session_id: sessionId,
          restaurant_name: restaurant.name,
          share_method: "native",
          is_demo: isDemoMode,
        });

        setSnackbar({
          open: true,
          message: "Thanks for sharing!",
          severity: "success",
        });
      } else {
        const shareText = `${shareData.text} ${shareData.url}`;
        await navigator.clipboard.writeText(shareText);

        posthog.capture("share_clicked", {
          session_id: sessionId,
          restaurant_name: restaurant.name,
          share_method: "clipboard",
          is_demo: isDemoMode,
        });

        setSnackbar({
          open: true,
          message: "Link copied to clipboard!",
          severity: "success",
        });
      }
    } catch (err) {
      if (err.name !== "AbortError") {
        console.error("Error sharing:", err);
        setSnackbar({
          open: true,
          message: "Unable to share. Please try again.",
          severity: "error",
        });
      }
    }
  };

  const handleCloseSnackbar = () => {
    setSnackbar({ ...snackbar, open: false });
  };

  const handleSocialClick = (platform) => {
    posthog.capture("social_media_clicked", {
      session_id: sessionId,
      restaurant_name: restaurant.name,
      platform: platform,
      is_demo: isDemoMode,
    });
  };

  // --- Simple loading / error states ---
  if (loading) {
    return (
      <Frame>
        {isDemoMode && (
          <DemoBanner>
            <DemoBannerText>
              <Eye size={16} />
              DEMO MODE
            </DemoBannerText>
          </DemoBanner>
        )}
        <ScrollArea>
          <Banner>
            <BannerImage src={Match} alt="It's a match" />
          </Banner>
          <Content>
            <Box
              display="flex"
              flexDirection="column"
              alignItems="center"
              mt={6}
            >
              <Box
                sx={{
                  width: 48,
                  height: 48,
                  borderRadius: "50%",
                  border: "4px solid",
                  borderColor: "warning.main",
                  borderTopColor: "transparent",
                  animation: "spin 1s linear infinite",
                  mb: 2,
                  "@keyframes spin": {
                    "0%": { transform: "rotate(0deg)" },
                    "100%": { transform: "rotate(360deg)" },
                  },
                }}
              />
              <Typography variant="body2" color="text.secondary">
                Loading your match...
              </Typography>
            </Box>
          </Content>
        </ScrollArea>
      </Frame>
    );
  }

  if (error || !restaurant) {
    return (
      <Frame>
        {isDemoMode && (
          <DemoBanner>
            <DemoBannerText>
              <Eye size={16} />
              DEMO MODE
            </DemoBannerText>
          </DemoBanner>
        )}
        <ScrollArea>
          <Banner>
            <BannerImage src={Match} alt="It's a match" />
          </Banner>
          <Content>
            <Typography variant="body2" color="error" mb={2}>
              {error || "No match found for this session."}
            </Typography>
            <StartOverButton onClick={handleStartOver}>
              Find Another Match!
            </StartOverButton>
          </Content>
        </ScrollArea>
      </Frame>
    );
  }

  return (
    <Frame>
      {/* Demo Mode Banner */}
      {isDemoMode && (
        <DemoBanner>
          <DemoBannerText>
            <Eye size={16} />
            DEMO MODE
          </DemoBannerText>
        </DemoBanner>
      )}

      <ScrollArea>
        {/* Banner with image */}
        <Banner>
          <BannerImage src={Match} alt="It's a match" />
        </Banner>

        {/* Image Gallery */}
        <ImageContainer>
          {restaurant.images?.length ? (
            <GalleryImage
              src={restaurant.images[currentImageIndex]}
              alt={restaurant.name}
            />
          ) : (
            <GalleryImage src={RestaurantAltImage} alt={restaurant.name} />
          )}

          {restaurant.images && restaurant.images.length > 1 && (
            <>
              <GalleryNavButtonLeft size="small" onClick={prevImage}>
                <ChevronLeft size={24} />
              </GalleryNavButtonLeft>

              <GalleryNavButtonRight size="small" onClick={nextImage}>
                <ChevronRight size={24} />
              </GalleryNavButtonRight>

              <Indicators>
                {restaurant.images.map((_, idx) => (
                  <IndicatorDot key={idx} active={idx === currentImageIndex} />
                ))}
              </Indicators>
            </>
          )}
        </ImageContainer>

        {/* Restaurant Info */}
        <Content>
          {/* Demo Mode Explanation */}
          {isDemoMode && (
            <DemoInfoBox>
              <DemoInfoTitle>
                <Eye size={18} />
                This is your match page!
              </DemoInfoTitle>
              <DemoInfoText>
                <strong>In a real session:</strong> When all participants like
                the same restaurant, everyone sees this match page together with
                the restaurant details.
              </DemoInfoText>
              <DemoInfoText>
                <strong>Ready to try it with someone?</strong> Scroll down and
                tap "Try With A Partner!" to return to the home page and start a
                session with your dining partner(s).
              </DemoInfoText>
            </DemoInfoBox>
          )}

          {/* Header */}
          <Box
            display="flex"
            justifyContent="space-between"
            alignItems="flex-start"
            mb={2}
          >
            <Box flex={1} pr={2}>
              <Typography variant="h5" fontWeight={700} gutterBottom>
                {restaurant.name}
              </Typography>

              <Stack direction="row" spacing={1} alignItems="center" mb={1}>
                {restaurant.cuisine && (
                  <>
                    <Typography
                      variant="body2"
                      color="warning.main"
                      fontWeight={600}
                    >
                      {restaurant.cuisine}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      •
                    </Typography>
                  </>
                )}

                {restaurant.priceRange && (
                  <>
                    <Typography variant="body2" color="text.secondary">
                      {restaurant.priceRange}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      •
                    </Typography>
                  </>
                )}

                {restaurant.distance && (
                  <Typography variant="body2" color="text.secondary">
                    {restaurant.distance}
                  </Typography>
                )}
              </Stack>

              {restaurant.rating && (
                <Stack direction="row" alignItems="center" spacing={1} mb={1.5}>
                  <Box display="flex">
                    {[...Array(5)].map((_, i) => {
                      const fullStars = Math.floor(Number(restaurant.rating));
                      return (
                        <Box key={i} mr={0.5}>
                          <svg
                            width="18"
                            height="18"
                            viewBox="0 0 24 24"
                            fill={i < fullStars ? "#FACC15" : "none"}
                            stroke={i < fullStars ? "#FACC15" : "#E5E7EB"}
                            strokeWidth="2"
                            strokeLinecap="round"
                            strokeLinejoin="round"
                          >
                            <polygon points="12 2 15.09 8.26 22 9.27 17 14.14 18.18 21.02 12 17.77 5.82 21.02 7 14.14 2 9.27 8.91 8.26 12 2" />
                          </svg>
                        </Box>
                      );
                    })}
                  </Box>
                  <Typography variant="body2" fontWeight={500}>
                    {restaurant.rating}
                    {restaurant.reviewCount
                      ? ` (${restaurant.reviewCount} ratings)`
                      : ""}
                  </Typography>
                </Stack>
              )}

              {/* Tags */}
              {restaurant.tags?.length > 0 && (
                <TagContainer>
                  {restaurant.tags.map((tag, idx) => (
                    <TagChip key={idx} label={tag} size="small" />
                  ))}
                </TagContainer>
              )}
            </Box>

            {/* Share + Social */}
            <Stack spacing={1} alignItems="flex-end">
              <ShareButton size="small" onClick={handleShare}>
                <Share2 size={18} />
              </ShareButton>

              {restaurant.social.instagram && (
                <InstagramButton
                  href={`https://instagram.com/${restaurant.social.instagram.replace(
                    "@",
                    ""
                  )}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  onClick={() => handleSocialClick("instagram")}
                >
                  <Instagram size={18} />
                </InstagramButton>
              )}

              {restaurant.social.facebook && (
                <FacebookButton
                  href={`https://facebook.com/${restaurant.social.facebook}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  onClick={() => handleSocialClick("facebook")}
                >
                  <Facebook size={18} />
                </FacebookButton>
              )}

              {restaurant.social.twitter && (
                <TwitterButton
                  href={`https://twitter.com/${restaurant.social.twitter.replace(
                    "@",
                    ""
                  )}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  onClick={() => handleSocialClick("twitter")}
                >
                  <Twitter size={18} />
                </TwitterButton>
              )}
            </Stack>
          </Box>

          {/* Description */}
          {restaurant.description && (
            <Typography
              variant="body2"
              color="text.secondary"
              mb={3}
              lineHeight={1.6}
            >
              {restaurant.description}
            </Typography>
          )}

          {/* Contact Info */}
          <Box mb={3}>
            <Stack spacing={1.5}>
              {(restaurant.address || restaurant.city) && (
                <Stack direction="row" spacing={1.5} alignItems="flex-start">
                  <MapPin size={20} color="#F97316" />
                  <Box onClick={handleDirections}>
                    {restaurant.address && (
                      <Typography variant="body1" fontWeight={600}>
                        {restaurant.address}
                      </Typography>
                    )}
                    {restaurant.city && (
                      <Typography variant="body2" color="text.secondary">
                        {restaurant.city}
                      </Typography>
                    )}
                  </Box>
                </Stack>
              )}

              {restaurant.phone && (
                <Stack direction="row" spacing={1.5} alignItems="center">
                  <Phone size={20} color="#F97316" />
                  <PhoneLink
                    href={`tel:${restaurant.phone}`}
                    onClick={() => {
                      posthog.capture("phone_clicked", {
                        session_id: sessionId,
                        restaurant_name: restaurant.name,
                        phone: restaurant.phone,
                        is_demo: isDemoMode,
                      });
                    }}
                  >
                    {restaurant.phone}
                  </PhoneLink>
                </Stack>
              )}

              {restaurant.website && (
                <Stack direction="row" spacing={1.5} alignItems="flex-start">
                  <Globe
                    size={20}
                    color="#F97316"
                    style={{ flexShrink: 0, marginTop: 2 }}
                  />
                  <Box flex={1} minWidth={0}>
                    <WebsiteLink
                      href={
                        restaurant.website.startsWith("http")
                          ? restaurant.website
                          : `https://${restaurant.website}`
                      }
                      target="_blank"
                      rel="noopener noreferrer"
                      onClick={() => {
                        posthog.capture("website_clicked", {
                          session_id: sessionId,
                          restaurant_name: restaurant.name,
                          website: restaurant.website,
                          is_demo: isDemoMode,
                        });
                      }}
                    >
                      {restaurant.website.replace(/^https?:\/\//, "")}
                      <ExternalLink size={14} style={{ flexShrink: 0 }} />
                    </WebsiteLink>
                  </Box>
                </Stack>
              )}
            </Stack>
          </Box>

          {/* Hours */}
          {(restaurant.hours || restaurant.hoursDisplay) && (
            <Box mb={3}>
              <HoursHeaderRow direction="row" spacing={1} alignItems="center">
                <Clock size={20} color="#F97316" />
                <Typography variant="subtitle1" fontWeight={700}>
                  Hours
                </Typography>
              </HoursHeaderRow>

              <Stack spacing={1}>
                <TodayRow>
                  <Typography variant="body2">Today</Typography>
                  <Typography variant="body2">
                    {restaurant.hours?.[currentDay] ||
                      restaurant.hoursDisplay ||
                      "See website for hours"}
                  </Typography>
                </TodayRow>

                {restaurant.hours && (
                  <>
                    {showAllHours && (
                      <Box pt={1}>
                        {Object.entries(restaurant.hours).map(
                          ([day, hours]) => {
                            if (day === currentDay) return null;
                            return (
                              <Box
                                key={day}
                                display="flex"
                                justifyContent="space-between"
                                mb={0.5}
                              >
                                <Typography
                                  variant="body2"
                                  color="text.secondary"
                                >
                                  {day}
                                </Typography>
                                <Typography variant="body2">{hours}</Typography>
                              </Box>
                            );
                          }
                        )}
                      </Box>
                    )}

                    <HoursToggleButton
                      variant="text"
                      size="small"
                      onClick={() => setShowAllHours((prev) => !prev)}
                    >
                      {showAllHours ? "− Show less" : "+ Show full week"}
                    </HoursToggleButton>
                  </>
                )}
              </Stack>
            </Box>
          )}

          <DeliveryLinks restaurant={restaurant} address={restaurant.address} />

          {/* Action Buttons */}
          <Box pb={2}>
            <Stack spacing={1.5}>
              <PrimaryActionButton
                onClick={handleViewMenu}
                disabled={!restaurant.menuUrl}
              >
                View Menu
              </PrimaryActionButton>

              <SecondaryActionButton onClick={handleStartOver}>
                {isDemoMode ? "Try With A Partner!" : "Find Another Match!"}
              </SecondaryActionButton>
            </Stack>
          </Box>
          <Box sx={{ mt: 3, mb: 2, display: "flex", justifyContent: "center" }}>
            <InstallButton />
          </Box>
        </Content>
      </ScrollArea>

      {/* Snackbar for share feedback */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={3000}
        onClose={handleCloseSnackbar}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
      >
        <Alert
          onClose={handleCloseSnackbar}
          severity={snackbar.severity}
          sx={{ width: "100%" }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Frame>
  );
};

export default MatchPage;
